import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, BookOpen, Sparkles, Heart, BarChart2, Users, ClipboardList } from 'lucide-react';
import type { Account, AccountsState } from '../types';
import { OnboardingWelcomeStep } from './onboarding/OnboardingWelcomeStep';
import type { OnboardingStepState, AccountRole } from '../types';
import { playSoundIntent } from '../../utils/sound/soundIntent';
import { hapticManager } from '../../utils/hapticManager';
import { getRoleThemeClasses } from '../logic/uiHelpers';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
// Ambient floating icons per role — subtle 'breathing' decorations behind the wizard card.
// Disabled when shouldAnimate=false (respects prefers-reduced-motion).
const AMBIENT_ICONS: Record<AccountRole, Array<{
  Icon: typeof Calculator;
  x: string;
  y: string;
  color: string;
  size: number;
  duration: number;
}>> = {
  learner: [{
    Icon: Calculator,
    x: '8%',
    y: '12%',
    color: 'text-orange-300',
    size: 32,
    duration: 11
  }, {
    Icon: BookOpen,
    x: '85%',
    y: '20%',
    color: 'text-amber-300',
    size: 28,
    duration: 9
  }, {
    Icon: Sparkles,
    x: '12%',
    y: '78%',
    color: 'text-orange-200',
    size: 24,
    duration: 13
  }],
  parent: [{
    Icon: BarChart2,
    x: '10%',
    y: '15%',
    color: 'text-emerald-300',
    size: 30,
    duration: 11
  }, {
    Icon: Heart,
    x: '82%',
    y: '22%',
    color: 'text-teal-300',
    size: 26,
    duration: 9
  }, {
    Icon: Sparkles,
    x: '14%',
    y: '76%',
    color: 'text-emerald-200',
    size: 24,
    duration: 13
  }],
  teacher: [{
    Icon: Users,
    x: '9%',
    y: '14%',
    color: 'text-indigo-300',
    size: 32,
    duration: 11
  }, {
    Icon: ClipboardList,
    x: '84%',
    y: '22%',
    color: 'text-blue-300',
    size: 28,
    duration: 9
  }, {
    Icon: Sparkles,
    x: '13%',
    y: '77%',
    color: 'text-indigo-200',
    size: 24,
    duration: 13
  }],
  admin: []
};
interface OnboardingWizardProps {
  account: Account;
  accounts: AccountsState;
  onCompleteOnboarding: (stepState?: OnboardingStepState) => void;
  onNavigateToModule?: (moduleId: string) => void;
  onNavigateToMyClass?: () => void;
  // Retained for backwards compatibility with callers (App.tsx + AuthGuard.tsx).
  // The wizard no longer triggers these actions itself — all classroom / child
  // linking is handled in the portals' empty-states.
  onJoinClassroom?: (joinCode: string) => {
    success: boolean;
    error?: string;
  };
  onLinkChild?: (linkCode: string) => {
    success: boolean;
    error?: string;
  };
  onCreateClassroom?: (name: string, grade: number) => void;
  onJoinExistingClassroom?: (classroomId: string, mode: 'transfer' | 'coteach') => void;
}
export function OnboardingWizard({
  account,
  accounts: _accounts,
  onCompleteOnboarding,
  onNavigateToModule,
  onNavigateToMyClass,
  onJoinClassroom: _onJoinClassroom,
  onLinkChild: _onLinkChild,
  onCreateClassroom: _onCreateClassroom,
  onJoinExistingClassroom: _onJoinExistingClassroom
}: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [groupLevel, setGroupLevel] = useState(account.role === 'learner' && account.grade ? account.grade : 6);
  const [stepState, setStepState] = useState<OnboardingStepState>({
    welcome: null,
    action: null,
    followUp: null
  });
  const theme = getRoleThemeClasses(account.role);
  const {
    shouldAnimate
  } = useMotionBudget();
  const totalSteps = 1; // All roles now use a 1-step wizard
  const stepLabel = `Stap ${currentStep + 1} van ${totalSteps}`;
  const ambientIcons = AMBIENT_ICONS[account.role] ?? [];
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <OnboardingWelcomeStep key="step0" role={account.role} name={account.displayName} groupLevel={groupLevel} totalSteps={totalSteps} onNext={() => {
          // One tap = one celebration moment. Previously this fired
          // playCorrect()+playLevelUp() and hapticMedium()+hapticSuccess()
          // back-to-back (App-Feel 2027 §3A / W19 — double sound/haptic).
          const newState = {
            ...stepState,
            welcome: 'completed' as const,
            action: 'skipped' as const,
            followUp: 'skipped' as const
          };
          setStepState(newState);
          playSoundIntent('reward.levelUp');
          hapticManager.hapticSuccess();
          onCompleteOnboarding(newState);
        }} onGroupChange={setGroupLevel} data-id="element-167" data-mp="k6ztb6rnavnd" />;
      default:
        return null;
    }
  };
  return <div className={`relative min-h-screen ${theme.background} flex flex-col items-center justify-center p-4 sm:p-8 overflow-hidden`} data-id="element-168" data-mp="1pfaqjy3qg1gm">
      {/* Ambient floating icons — subtle, role-themed, drift slowly */}
      {shouldAnimate && ambientIcons.length > 0 && <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true" data-id="element-169" data-mp="1kg7yrfcrjrql">
          {ambientIcons.map(({
        Icon,
        x,
        y,
        color,
        size,
        duration
      }, i) => <motion.div key={i} className={`absolute opacity-30 ${color}`} style={{
        left: x,
        top: y
      }} animate={{
        y: [0, -14, 0],
        rotate: [0, 6, 0]
      }} transition={{
        duration,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 1.4
      }} data-id="element-170" data-mp="2d4wtmwn870tw" data-mpi={i}>
              <Icon size={size} strokeWidth={1.5} data-id="element-171" data-mp="2uehdywdu28zc" />
            </motion.div>)}
        </div>}

      {/* Stage label — desktop-only, very subtle */}
      <div className="hidden sm:block absolute top-6 left-1/2 -translate-x-1/2 z-10 pointer-events-none" data-id="element-172" data-mp="2pg66gsb0gaka">
        <AnimatePresence mode="wait" data-id="element-173" data-mp="2kmuu6bnfj1kz">
          <motion.span key={stepLabel} initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.25
        }} className={`text-[11px] font-bold uppercase tracking-[0.18em] ${account.role === 'learner' ? 'text-orange-500/70' : account.role === 'parent' ? 'text-emerald-600/70' : 'text-indigo-600/70'}`} data-id="element-174" data-mp="2fput6f5fsp0q">
            {stepLabel}
          </motion.span>
        </AnimatePresence>
      </div>

      <div className="relative z-20 w-full flex justify-center" data-id="element-175" data-mp="3r2x8l6vsv8jb">
        <AnimatePresence mode="wait" data-id="element-176" data-mp="3fux13tfh32l">{renderStep()}</AnimatePresence>
      </div>
    </div>;
}